import { isEmpty } from 'lodash';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, Trans } from '@lyearn/i18n';
import { PeopleOutline, PersonEditOutline, ShuffleFill, TagOutline } from '@lyearn/icons';
import { SettingsTabType } from '@/components/Form/components/VisibilitySettings/components/VisibilitySettingsTabs/types';
import { ResourceReviewTypeEnum, ReviewerConfigInput, ReviewerTypeEnum } from '@/types/schema';

const validateSpecificReviewerError = (reviewerConfig: ReviewerConfigInput) => {
  const { type, defaultReviewerId, userIds } = reviewerConfig;

  if (type === ReviewerTypeEnum.Specific && !defaultReviewerId && (userIds ?? []).length > 1) {
    return <Trans i18nKey="Exercise:Creator.DefaultReviewerCantBeEmpty" />;
  }

  return null;
};

const validateReviewersError = (reviewerConfig: ReviewerConfigInput) => {
  const { userIds, defaultReviewerId } = reviewerConfig;

  if (!userIds?.length && !defaultReviewerId) {
    return <Trans i18nKey="Exercise:Creator.ReviewerCantBeEmpty" />;
  }

  return null;
};

const validateReviewerTagError = (reviewerConfig: ReviewerConfigInput) => {
  const { type, tagId, fieldName } = reviewerConfig;

  if (type === ReviewerTypeEnum.Dynamic && !tagId && !fieldName) {
    return <Trans i18nKey="Exercise:Creator.TagCantBeEmpty" />;
  }

  return null;
};

export const validateReviewerPeerError = (reviewerConfig: ReviewerConfigInput) => {
  const { type, minimumReviewsRequired } = reviewerConfig;

  if (type === ReviewerTypeEnum.Peer) {
    if (!minimumReviewsRequired || !(minimumReviewsRequired >= 1 && minimumReviewsRequired <= 9)) {
      return <Trans i18nKey="Exercise:Creator.PeerReviewError" />;
    }
  }

  return null;
};

const validateReviewerGroupError = (reviewerConfig: ReviewerConfigInput) => {
  const { type, userGroupIds } = reviewerConfig;

  if (type === ReviewerTypeEnum.Group && isEmpty(userGroupIds)) {
    return <Trans i18nKey="Exercise:Creator.ReviewerGroupCantBeEmpty" />;
  }

  return null;
};

const validateDefaultReviewerError = (reviewerConfig: ReviewerConfigInput) => {
  const { type, defaultReviewerId } = reviewerConfig;

  if ([ReviewerTypeEnum.Dynamic, ReviewerTypeEnum.Group].includes(type) && !defaultReviewerId) {
    return <Trans i18nKey="Exercise:Creator.DefaultReviewerCantBeEmpty" />;
  }

  return null;
};

const validateReviewerConfigError = (
  reviewerConfig?: ReviewerConfigInput | null,
  reviewType?: ResourceReviewTypeEnum | null,
) => {
  if (!reviewerConfig || reviewType === ResourceReviewTypeEnum.NoReview) {
    return null;
  }

  if (
    validateSpecificReviewerError(reviewerConfig) ||
    validateReviewerTagError(reviewerConfig) ||
    validateReviewersError(reviewerConfig) ||
    validateReviewerGroupError(reviewerConfig) ||
    validateDefaultReviewerError(reviewerConfig) ||
    validateReviewerPeerError(reviewerConfig)
  ) {
    return true;
  }

  return false;
};

const getSettingsTabs = (t: TFunction, reviewType: ResourceReviewTypeEnum): SettingsTabType[] =>
  getEnabledItems([
    {
      id: ReviewerTypeEnum.Specific,
      label: t('Exercise:Creator.SpecificReviewers'),
      Icon: PersonEditOutline,
      enabled: true,
    },
    {
      id: ReviewerTypeEnum.Dynamic,
      label: t('Exercise:Creator.DynamicReviewer'),
      Icon: TagOutline,
      enabled: true,
    },
    {
      id: ReviewerTypeEnum.Group,
      label: t('Exercise:Creator.GroupReviewer'),
      Icon: PeopleOutline,
      enabled: true,
    },
    {
      id: ReviewerTypeEnum.Peer,
      label: t('Exercise:Creator.PeerReviewer'),
      Icon: ShuffleFill,
      enabled: reviewType !== ResourceReviewTypeEnum.OptionalReview,
    },
  ]);

const adaptReviewerConfigOnSubmit = (
  reviewType: ResourceReviewTypeEnum,
  reviewerConfig: ReviewerConfigInput,
): ReviewerConfigInput | undefined => {
  if (reviewType !== ResourceReviewTypeEnum.NoReview) {
    const {
      type,
      tagId,
      fieldName,
      defaultReviewerId,
      userGroupIds,
      minimumReviewsRequired,
      anonymousReview,
      enableAiReview,
      userIds,
    } = reviewerConfig;

    switch (type) {
      case ReviewerTypeEnum.Dynamic:
        return { type, tagId, fieldName, defaultReviewerId, enableAiReview };
      case ReviewerTypeEnum.Group:
        return { type, userGroupIds, defaultReviewerId, enableAiReview };
      case ReviewerTypeEnum.Peer:
        return { type, minimumReviewsRequired, anonymousReview, enableAiReview };
      case ReviewerTypeEnum.Specific:
        return { type, userIds, defaultReviewerId, enableAiReview };
    }
  }

  return undefined;
};

export {
  adaptReviewerConfigOnSubmit,
  getSettingsTabs,
  validateDefaultReviewerError,
  validateReviewerConfigError,
  validateReviewerGroupError,
  validateReviewersError,
  validateReviewerTagError,
  validateSpecificReviewerError,
};
